// ─────────────────────────────────────────────────────────────────────────────
// The slice of YouTube's IFrame Player API both players use, typed once.
//
// `window.YT` is a single global, so it can only carry one declaration — the
// two surfaces (JumpPlayer's supercut, SwipeReel's stack) share this one rather
// than each declaring its own and disagreeing about the shape.
//
// The API script is loaded at most once per page, however many players mount:
// `onYouTubeIframeAPIReady` fires exactly once, so the promise is memoised and
// every caller awaits the same load.
// ─────────────────────────────────────────────────────────────────────────────

export type YTPlayer = {
  loadVideoById(o: {videoId: string; startSeconds: number; endSeconds: number}): void
  cueVideoById(o: {videoId: string; startSeconds: number; endSeconds: number}): void
  seekTo(seconds: number, allowSeekAhead: boolean): void
  playVideo(): void
  pauseVideo(): void
  mute(): void
  unMute(): void
  destroy(): void
}

export type YTPlayerOptions = {
  width: string
  height: string
  videoId?: string
  playerVars: Record<string, string | number>
  events: {
    onReady?: (e: {target: YTPlayer}) => void
    onStateChange?: (e: {data: number; target: YTPlayer}) => void
    onError?: (e: {data: number; target: YTPlayer}) => void
  }
}

export type YTNamespace = {
  Player: new (el: HTMLElement, opts: YTPlayerOptions) => YTPlayer
  PlayerState: {
    UNSTARTED: number
    ENDED: number
    PLAYING: number
    PAUSED: number
    BUFFERING: number
    CUED: number
  }
}

declare global {
  interface Window {
    YT?: YTNamespace
    onYouTubeIframeAPIReady?: () => void
  }
}

let apiPromise: Promise<YTNamespace> | null = null

export function loadIframeApi(): Promise<YTNamespace> {
  if (apiPromise) return apiPromise
  apiPromise = new Promise(resolve => {
    if (window.YT?.Player) {
      resolve(window.YT)
      return
    }
    // Chain rather than clobber: another surface on the page may already be
    // waiting on this callback.
    const prev = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      prev?.()
      if (window.YT) resolve(window.YT)
    }
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    document.head.appendChild(tag)
  })
  return apiPromise
}
